import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { orderApiService } from "../../../service/admin/ordersApiService";
import { useLoader } from "../../../base/context/LoaderProvider";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaSync, FaFilter, FaTicketAlt, FaFileExport } from "react-icons/fa";
import RefundModal from "../../../component/views/admin/RefundModel";
import moment from "moment";
import ExcelJS from "exceljs";
import "../imPointsManagement/style.css";
import "./ticketManagement.css";

// Calculate importer price from order details (finalPrice or breakdown.FBP)
const calculateImporterrPrice = (order) => {
  if (!order || !Array.isArray(order.orderDetails)) {
    return 0;
  }

  let productTotal = 0;
  
  // Check for wholesale breakdown
  const wholesaleBreakdownMap = new Map();
  if (Array.isArray(order.breakdown)) {
    for (const breakdownItem of order.breakdown) {
      if (breakdownItem?.pricingMode === 'wholesale' && breakdownItem?.productId && breakdownItem?.breakdown?.[0]?.FBP) {
        wholesaleBreakdownMap.set(breakdownItem.productId.toString(), Number(breakdownItem.breakdown[0].FBP) || 0);
      }
    }
  }

  // Group orderDetails by product
  const productGroups = new Map();
  order.orderDetails.forEach(detail => {
    const productId = detail?.product?._id?.toString() || detail?.product?.toString() || '';
    if (!productGroups.has(productId)) {
      productGroups.set(productId, []);
    }
    productGroups.get(productId).push(detail);
  });

  // Calculate total for each product group
  productGroups.forEach((details, productId) => {
    const wholesaleFBP = wholesaleBreakdownMap.get(productId);
    if (wholesaleFBP) {
      // Wholesale product - use wholesale breakdown FBP
      productTotal += wholesaleFBP;
    } else {
      // Dropshipping product - use finalPrice or fallback to breakdown.FBP
      details.forEach(detail => {
        const variant = detail?.variant;
        let price = 0;
        if (variant?.finalPrice != null && Number(variant.finalPrice) > 0) {
          price = Number(variant.finalPrice);
        } else {
          const breakdown = variant?.breakdown;
          price = Number(breakdown?.FBP ?? breakdown?.fbp) || 0;
        }
        const quantity = Number(detail?.quantity) || 1;
        productTotal += price * quantity;
      });
    }
  });

  return productTotal;
};

const TicketManagement = () => {
  const loader = useLoader();
  const message = useMessage();
  const navigate = useNavigate();
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [orderStatus, setOrderStatus] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [pages, setPages] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  });
  const [ticketData, setTicketData] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);
  
  // Refund Modal State
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const openModal = (ticket) => {
    const order = ticket?.orderId;
    const importerrPrice = calculateImporterrPrice(order);
    const shippingCharges = Number(order?.totalShippingCharges) || 0;
    
    const orderData = {
      orderId: order?._id,
      orderAmount: importerrPrice,
      shippingCharges: shippingCharges,
      totalAmount: importerrPrice + shippingCharges,
      sellerId: order?.sellerId,
      upsaleoOrderId: order?.upsaleoOrderId,
      ticketId: ticket?._id,
    };
    setSelectedOrder(orderData);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedOrder(null);
  };

  const handleRefundSuccess = () => {
    closeModal();
    fetchTicketList();
    message.showToast("Refund processed successfully", ToastTypes.SUCCESS);
  };

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    params.set("page", page);
    params.set("limit", limit);
    if (status) params.set("status", status);
    if (orderStatus) params.set("orderStatus", orderStatus);
    if (stateFilter) params.set("state", stateFilter);
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    if (search) params.set("search", search);
    return `?${params.toString()}`;
  }, [page, limit, status, orderStatus, stateFilter, startDate, endDate, search]);

  const fetchTicketList = useCallback(() => {
    setLoading(true);
    loader.showInlineLoader("Loading tickets...", "medium");
    orderApiService
      .getAllTicket(buildQuery())
      .then((res) => {
        loader.hideLoader();
        setLoading(false);
        setInitialLoading(false);
        if (res.status.code === 200) {
          setTicketData(res.data.ticket);
          setPages({
            currentPage: res.data.currentPage || 1,
            totalPages: res.data.totalPages || 1,
            totalItems: res.data.totalTicket || 0,
          });
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
        loader.hideLoader();
        setLoading(false);
        setInitialLoading(false);
      });
  }, [loader, message, buildQuery]);

  useEffect(() => {
    fetchTicketList();
  }, [fetchTicketList]);

  useEffect(() => {
    const handleOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
    };
  }, []);

  // Transform data for table
  const tableData = useMemo(() => {
    return ticketData.map((item, index) => {
      const order = item?.orderId;
      const importerrPrice = calculateImporterrPrice(order);
      const shippingCharges = Number(order?.totalShippingCharges) || 0;
      
      const seller = order?.sellerId && typeof order.sellerId === "object" ? order.sellerId : null;
      const sellerFullName = seller
        ? `${seller.firstName || ""} ${seller.lastName || ""}`.trim()
        : "";
      const sellerDisplayName = sellerFullName || order?.recipientName || "-";

      return {
        ...item,
        serialNumber: (pages.currentPage - 1) * limit + index + 1,
        importerrOrderId: order?.upsaleoOrderId || order?._id || "-",
        sellerName: sellerDisplayName,
        sellerUniqueId: seller?.uniqueId || "",
        sellerEmail: seller?.email || "",
        orderDate: order?.orderDate,
        orderStatus: order?.status || "-",
        ticketStatus: item?.status || "-",
        state: order?.shippingState || "-",
        ticketCreatedDate: item?.updatedAt,
        orderAmount: importerrPrice + shippingCharges,
      };
    });
  }, [ticketData, pages.currentPage, limit]);

  const formatCurrency = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return "₹0.00";
    return `₹${amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getStatusBadge = (statusValue) => {
    const statusConfig = {
      open: { color: "#f59e0b" },
      closed: { color: "#10b981" },
      processing: { color: "#3b82f6" },
      refunded: { color: "#8b5cf6" },
      "in-progress": { color: "#06b6d4" },
    };

    const key = String(statusValue || "").toLowerCase();
    const config = statusConfig[key] || { color: "#6b7280" };

    return (
      <span
        className="status-badge"
        style={{
          backgroundColor: `${config.color}20`,
          color: config.color,
          padding: "3px 9px",
          borderRadius: "10px",
          fontSize: "10px",
          fontWeight: "600",
          textTransform: "uppercase",
        }}
      >
        {statusValue || "-"}
      </span>
    );
  };

  const renderPageButtons = () => {
    const buttons = [];
    const start = Math.max(1, pages.currentPage - 2);
    const end = Math.min(pages.totalPages, pages.currentPage + 2);

    for (let i = start; i <= end; i++) {
      buttons.push(
        <button key={i} className={`imp-page-btn ${i === pages.currentPage ? "active" : ""}`} onClick={() => setPage(i)}>
          {i}
        </button>
      );
    }
    return buttons;
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setStatus("");
    setOrderStatus("");
    setStateFilter("");
    setStartDate("");
    setEndDate("");
    setPage(1);
  };

  const buildExportRow = (item, index) => {
    const order = item?.orderId;
    const importerrPrice = calculateImporterrPrice(order);
    const shippingCharges = Number(order?.totalShippingCharges) || 0;
    const seller = order?.sellerId && typeof order.sellerId === "object" ? order.sellerId : null;
    const sellerFullName = seller
      ? `${seller.firstName || ""} ${seller.lastName || ""}`.trim()
      : "";
    return {
      "S.No.": index + 1,
      "Order ID": order?.upsaleoOrderId || order?._id || "-",
      "Seller Name": sellerFullName || order?.recipientName || "-",
      "Unique ID": seller?.uniqueId || "-",
      "Email": seller?.email || "-",
      "Order Status": order?.status || "-",
      "Ticket Status": item?.status || "-",
      "State": order?.shippingState || "-",
      "Importerr Price": Number((importerrPrice + shippingCharges).toFixed(2)),
      "Order Date": order?.orderDate ? moment(order.orderDate).format("DD/MM/YYYY, LT") : "-",
      "Ticket Updated": item?.updatedAt ? moment(item.updatedAt).format("DD/MM/YYYY, LT") : "-",
    };
  };

  const downloadExcel = async (rows, mode) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Tickets");
    const headers = rows.length ? Object.keys(rows[0]) : [];

    worksheet.addRow(headers);
    rows.forEach((row) => worksheet.addRow(headers.map((h) => row[h])));

    // Header style
    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4F46E5" } };
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Data style + dynamic width
    headers.forEach((header, colIdx) => {
      let maxLen = String(header).length;
      rows.forEach((row) => {
        const val = row[header] ?? "";
        maxLen = Math.max(maxLen, String(val).length);
      });
      const column = worksheet.getColumn(colIdx + 1);
      column.width = Math.min(48, Math.max(12, maxLen + 2));
    });

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;
      row.eachCell((cell) => {
        cell.font = { size: 10 };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    const timestamp = moment().format("YYYYMMDD_HHmmss");
    a.href = url;
    a.download = `ticket_management_${mode}_${timestamp}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const fetchExportTickets = async (mode) => {
    const shouldUseDate = mode === "date-wise";
    if (shouldUseDate && (!startDate || !endDate)) {
      message.showToast("Please select Start Date and End Date for date-wise export", ToastTypes.WARNING);
      return;
    }

    loader.showLoading(true);
    try {
      const batchLimit = 500;
      let currentPage = 1;
      let totalPages = 1;
      const allRows = [];

      while (currentPage <= totalPages) {
        const params = new URLSearchParams();
        params.set("page", currentPage);
        params.set("limit", batchLimit);
        if (status) params.set("status", status);
        if (orderStatus) params.set("orderStatus", orderStatus);
        if (stateFilter) params.set("state", stateFilter);
        if (search) params.set("search", search);
        if (shouldUseDate) {
          params.set("startDate", startDate);
          params.set("endDate", endDate);
        }

        const res = await orderApiService.getAllTicket(`?${params.toString()}`);
        if (res?.status?.code !== 200) {
          throw new Error(res?.error?.message || "Failed to fetch export data");
        }
        const list = res?.data?.ticket || [];
        totalPages = res?.data?.totalPages || 1;

        list.forEach((item) => {
          allRows.push(item);
        });
        currentPage += 1;
      }

      if (!allRows.length) {
        message.showToast("No ticket data found for export", ToastTypes.WARNING);
        return;
      }

      const exportRows = allRows.map((item, idx) => buildExportRow(item, idx));
      await downloadExcel(exportRows, mode);
      message.showToast("Excel exported successfully", ToastTypes.SUCCESS);
    } catch (err) {
      message.showToast(err?.message || "Failed to export excel", ToastTypes.ERROR);
    } finally {
      loader.showLoading(false);
      setShowExportMenu(false);
    }
  };

  return (
    <div className="imp-page ticket-management-page">
      <div className="imp-header">
        <h4>
          <FaTicketAlt style={{ marginRight: 6, color: "#4f46e5" }} />
          Ticket Management
        </h4>
        <button className="imp-btn imp-btn-outline" onClick={() => fetchTicketList()} title="Refresh">
          <FaSync size={10} />
        </button>
      </div>

      <div className="imp-filter-bar">
        <form onSubmit={onSearchSubmit} className="imp-search-form">
          <div className="imp-search-wrap">
            <FaSearch />
            <input
              className="imp-search-input"
              placeholder="Search by Order ID, seller name, email, unique ID..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <button type="submit" className="imp-btn imp-btn-primary">
            Search
          </button>
        </form>

        <select className="imp-filter-select" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All Ticket Status</option>
          <option value="open">Open</option>
          <option value="in-progress">In Progress</option>
          <option value="closed">Closed</option>
        </select>

        <select className="imp-filter-select" value={orderStatus} onChange={(e) => { setOrderStatus(e.target.value); setPage(1); }}>
          <option value="">All Order Status</option>
          <option value="pending">Pending</option>
          <option value="processing">Processing</option>
          <option value="shipped">Shipped</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
          <option value="refunded">Refunded</option>
        </select>

        <input className="imp-date-input" type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} />
        <input className="imp-date-input" type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} />

        <input
          className="imp-filter-select"
          style={{ width: 120 }}
          placeholder="State"
          value={stateFilter}
          onChange={(e) => { setStateFilter(e.target.value); setPage(1); }}
        />

        <div className="ticket-export-anchor" ref={exportMenuRef}>
          <select className="imp-filter-select" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}>
            <option value={10}>10 rows</option>
            <option value={20}>20 rows</option>
            <option value={50}>50 rows</option>
            <option value={100}>100 rows</option>
          </select>
          <button
            type="button"
            className="imp-btn imp-btn-outline ticket-export-btn"
            onClick={() => setShowExportMenu((prev) => !prev)}
          >
            <FaFileExport size={11} />
            Export
          </button>
          {showExportMenu && (
            <div className="ticket-export-popover">
              <button type="button" className="ticket-export-option" onClick={() => fetchExportTickets("date-wise")}>
                Date wise
              </button>
              <button type="button" className="ticket-export-option" onClick={() => fetchExportTickets("all")}>
                All
              </button>
            </div>
          )}
        </div>

        {(search || status || orderStatus || stateFilter || startDate || endDate) && (
          <button className="imp-btn imp-btn-outline imp-btn-sm" onClick={clearFilters}>
            <FaFilter size={9} />
            Clear
          </button>
        )}
      </div>

      <div className="imp-table-wrap">
        <table className="imp-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Order ID</th>
              <th>Seller</th>
              <th>Order Status</th>
              <th>Ticket Status</th>
              <th>State</th>
              <th>Importerr Price</th>
              <th>Order Date</th>
              <th>Ticket Updated</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {initialLoading || loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="imp-loading-row">
                  {Array.from({ length: 10 }).map((__, j) => (
                    <td key={j}>
                      <span className="imp-skeleton" />
                    </td>
                  ))}
                </tr>
              ))
            ) : tableData.length === 0 ? (
              <tr className="imp-no-data">
                <td colSpan={10}>No tickets found for selected filters.</td>
              </tr>
            ) : (
              tableData.map((row) => (
                <tr key={row?._id}>
                  <td>{row.serialNumber}</td>
                  <td>
                    <button type="button" className="ticket-link-btn" onClick={() => navigate(`/admin/order-management/details/${row?.orderId?._id}`)}>
                      {row.importerrOrderId}
                    </button>
                  </td>
                  <td>
                    <div className="ticket-seller-line1">
                      {row.sellerName} {row.sellerUniqueId ? `(${row.sellerUniqueId})` : ""}
                    </div>
                    <div className="ticket-seller-line2">{row.sellerEmail || "-"}</div>
                  </td>
                  <td>{getStatusBadge(row.orderStatus)}</td>
                  <td>{getStatusBadge(row.ticketStatus)}</td>
                  <td>{row.state || "-"}</td>
                  <td style={{ color: "#059669", fontWeight: 600 }}>{formatCurrency(row.orderAmount)}</td>
                  <td>{row.orderDate ? moment(row.orderDate).format("DD/MM/YYYY, LT") : "-"}</td>
                  <td>{row.ticketCreatedDate ? moment(row.ticketCreatedDate).format("DD/MM/YYYY, LT") : "-"}</td>
                  <td>
                    <div className="ticket-actions-cell">
                      <button type="button" className="ticket-action-btn ticket-action-btn--view" onClick={() => navigate(`/admin/ticket-management/ticket/${row?._id}`)}>
                        View ticket
                      </button>
                      {row.status === "closed" ? (
                        <button type="button" className="ticket-action-btn ticket-action-btn--view-refund" onClick={() => navigate(`/admin/ticket-management/refund/${row?.orderId?._id}`)}>
                          View refund
                        </button>
                      ) : (
                        <button type="button" className="ticket-action-btn ticket-action-btn--refund" onClick={() => openModal(row)}>
                          Refund
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pages.totalPages > 1 && (
        <div className="imp-pagination">
          <span>
            Page {pages.currentPage} of {pages.totalPages} · {pages.totalItems} records
          </span>
          <div className="imp-pagination-btns">
            <button className="imp-page-btn" disabled={pages.currentPage <= 1} onClick={() => setPage((p) => p - 1)}>
              ‹
            </button>
            {renderPageButtons()}
            <button className="imp-page-btn" disabled={pages.currentPage >= pages.totalPages} onClick={() => setPage((p) => p + 1)}>
              ›
            </button>
          </div>
        </div>
      )}

      <RefundModal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        orderData={selectedOrder}
        onSuccess={handleRefundSuccess}
      />
    </div>
  );
};

export default TicketManagement;
